import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';

const API = process.env.REACT_APP_API_URL || '';

// Página pública — sin sesión, sin Layout. El paciente llega acá desde el link del
// email (?t=token&a=confirmar|rechazar) y no debería tener que hacer nada más que
// esperar: el POST se dispara solo, con JavaScript, al cargar la página. Un link de
// email simple (GET) que un escáner de correo pre-visite no alcanza a disparar esto.
const C = {
  fondo: '#17192B',
  card: '#202A46',
  texto: '#edf2f7',
  muted: '#8aa0b8',
  acento: '#34d399',
  error: '#ef5350',
};

export default function ConfirmarAsistencia() {
  const [estado, setEstado] = useState('cargando'); // cargando | ok | error
  const [mensaje, setMensaje] = useState('');
  const [turno, setTurno] = useState(null);
  const [resultado, setResultado] = useState(null); // 'confirmada' | 'rechazada'

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('t');
    const accion = params.get('a');

    if (!token) { setEstado('error'); setMensaje('Este link no es válido.'); return; }

    (async () => {
      try {
        const r = await axios.get(`${API}/api/public/confirmacion-asistencia/${token}`);
        setTurno(r.data.turno);

        if (r.data.ya_respondido) {
          setResultado(r.data.estado);
          setEstado('ok');
          return;
        }

        if (accion === 'confirmar' || accion === 'rechazar') {
          const r2 = await axios.post(`${API}/api/public/confirmacion-asistencia/${token}/${accion}`);
          setResultado(r2.data.estado);
          setEstado('ok');
        } else {
          setResultado(r.data.estado);
          setEstado('ok');
        }
      } catch (err) {
        setEstado('error');
        setMensaje(err.response?.data?.error || 'No pudimos procesar tu respuesta. El link puede haber vencido.');
      }
    })();
  }, []);

  return (
    <Box sx={{
      minHeight: '100vh', background: C.fondo,
      display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
    }}>
      <Paper elevation={0} sx={{
        background: C.card, backgroundImage: 'none', borderRadius: '14px',
        p: '32px', width: '100%', maxWidth: 380, textAlign: 'center',
        border: '1px solid rgba(255,255,255,0.065)', boxShadow: '0 4px 32px rgba(0,0,0,0.55)',
      }}>
        {estado === 'cargando' && (
          <>
            <CircularProgress size={28} sx={{ color: C.acento, mb: 2 }} />
            <Typography sx={{ color: C.muted, fontSize: 14 }}>Procesando tu respuesta...</Typography>
          </>
        )}

        {estado === 'error' && (
          <>
            <Typography sx={{ fontSize: 40, mb: 1 }}>⚠️</Typography>
            <Typography sx={{ color: C.texto, fontWeight: 700, fontSize: 17, mb: 1 }}>
              No pudimos procesar tu respuesta
            </Typography>
            <Typography sx={{ color: C.muted, fontSize: 13.5, lineHeight: 1.5 }}>
              {mensaje}
            </Typography>
          </>
        )}

        {estado === 'ok' && resultado === 'confirmada' && (
          <>
            <Typography sx={{ fontSize: 40, mb: 1 }}>✅</Typography>
            <Typography sx={{ color: C.texto, fontWeight: 700, fontSize: 18, mb: 1 }}>
              Gracias.
            </Typography>
            <Typography sx={{ color: C.muted, fontSize: 14, lineHeight: 1.5 }}>
              Su asistencia quedó confirmada
              {turno ? ` para el ${turno.fecha_larga} a las ${turno.hora}${turno.medico_nombre ? ` con ${turno.medico_nombre}` : ''}.` : '.'}
            </Typography>
          </>
        )}

        {estado === 'ok' && resultado === 'rechazada' && (
          <>
            <Typography sx={{ fontSize: 40, mb: 1 }}>📩</Typography>
            <Typography sx={{ color: C.texto, fontWeight: 700, fontSize: 18, mb: 1 }}>
              Gracias por avisar.
            </Typography>
            <Typography sx={{ color: C.muted, fontSize: 14, lineHeight: 1.5 }}>
              Quedó registrado que no podrá asistir
              {turno ? ` al turno del ${turno.fecha_larga} a las ${turno.hora}.` : '.'} El consultorio se pondrá en contacto si hace falta reprogramar.
            </Typography>
          </>
        )}

        {estado === 'ok' && resultado === 'pendiente' && (
          <>
            <Typography sx={{ fontSize: 40, mb: 1 }}>🕓</Typography>
            <Typography sx={{ color: C.texto, fontWeight: 700, fontSize: 17, mb: 1 }}>
              Todavía no respondiste
            </Typography>
            <Typography sx={{ color: C.muted, fontSize: 13.5, lineHeight: 1.5 }}>
              Usá los botones del email para confirmar o avisar que no podrás asistir.
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
}
